import { useEffect, useRef, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const SHOW_MENU_AFTER_IN_SECONDS = 10;

export const useHiddenScrollingClass = (): {
  isMobile: number | boolean;
  shouldHideHeader: boolean;
  scrollDirection: string | null;
} => {
  const isMobile = useMediaQuery(901);
  const [userScrolledPastThreshold, setUserScrolledPastThreshold] = useState(false);
  const [shouldHideHeader, setShouldHideHeader] = useState(false);
  const scrollPosition = useRef(0);
  const [scrollDirection, setScrollDirection] = useState<"up" | "down" | null>(null);
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const updateScrollDirection = () => {
      const scrollY = window.scrollY;
      const direction = scrollY > lastScrollY ? "down" : "up";

      // Hide header only when user scroll more than 650px
      if (scrollY >= window.screen.height - 650) {
        setUserScrolledPastThreshold(true);
      } else {
        setUserScrolledPastThreshold(false);
      }

      if (direction !== scrollDirection && (scrollY - lastScrollY > 5 || scrollY - lastScrollY < -5)) {
        setScrollDirection(direction);
      }
      lastScrollY = scrollY > 0 ? scrollY : 0;
    };
    window.addEventListener("scroll", updateScrollDirection);

    return () => {
      window.removeEventListener("scroll", updateScrollDirection);
    };
  }, [scrollDirection]);

  useEffect(() => {
    const checkUserIdle = () => {
      const scrollY = window.scrollY;
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }

      // Check if the user stays at the same position for SHOW_MENU_AFTER_IN_SECONDS seconds
      if (scrollY - scrollPosition.current < 10 && isMobile) {
        timeoutId.current = setTimeout(() => {
          setShouldHideHeader(false);
        }, SHOW_MENU_AFTER_IN_SECONDS * 1000);
      } else {
        setShouldHideHeader(isMobile && userScrolledPastThreshold && scrollDirection === "down");
      }
      scrollPosition.current = scrollY;
    };

    window.addEventListener("scroll", checkUserIdle);

    return () => {
      window.removeEventListener("scroll", checkUserIdle);
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
  }, [isMobile, scrollDirection, userScrolledPastThreshold]);

  useEffect(() => {
    setShouldHideHeader(isMobile && userScrolledPastThreshold && scrollDirection === "down");
  }, [isMobile, userScrolledPastThreshold, scrollDirection]);

  return { isMobile, shouldHideHeader, scrollDirection };
};
